/*
 * Copyright (c) 2019. ProVision Media Group Ltd. <https://provision.bg>
 * Venelin Iliev <http://veneliniliev.com>
 */

import { Component, OnInit } from '@angular/core';
import { Logger } from '@app/core';
import { KendoGridComponent } from '@app/shared/kendo/kendo-grid.component';
import { KendoGridService } from '@app/shared/kendo/kendo-grid.service';
import { RequestsService } from '@app/sales/requests/requests.service';
import { RequestsFormService } from '@app/sales/requests/requests-form.service';
import { DataStateChangeEvent, GridDataResult } from '@progress/kendo-angular-grid';
import { PaginatedResponse } from '@app/shared/models/paginated-response.model';
import { ApiQuery } from '@app/core/http/api-query';
import { LaravelModelsEnum } from '@app/shared/models/model.model';
import { RequestModel } from '@app/sales/requests/request.model';
import { SalesFormService } from '@app/sales/sales/sales-form.service';
import { environment } from '@env/environment';
import { ActivatedRoute, Router } from '@angular/router';
import { DataSet } from '@app/shared/kendo/data-set';
import { CustomKendoFilter } from '@app/shared/kendo/custom-kendo-filter';
import { AccessService } from '@app/core/access/access.service';
import { UserRoleType } from '@app/settings/users/user-role-type.enum';

const log = new Logger('RequestsListComponent');

@Component({
  selector: 'app-requests-list',
  templateUrl: './requests-list.component.html',
  styleUrls: ['./requests-list.component.scss'],
})
export class RequestsListComponent extends KendoGridComponent implements OnInit {
  requestsResponse: PaginatedResponse;
  data: GridDataResult;
  state: DataStateChangeEvent;
  apiQuery: ApiQuery = new ApiQuery();
  LaravelModels = LaravelModelsEnum;
  env = environment;
  UserRoleType = UserRoleType;

  constructor(
    private requestsService: RequestsService,
    private requestsFormService: RequestsFormService,
    protected kendoGridService: KendoGridService,
    private salesFormService: SalesFormService,
    private route: ActivatedRoute,
    private router: Router,
    public accessService: AccessService
  ) {
    super(kendoGridService);
  }

  ngOnInit() {
    this.route.queryParamMap.subscribe((params: any) => {
      this.state = CustomKendoFilter.getStateFromStorage('RequestsListComponent');

      if (params.get('id')) {
        this.state = new DataSet().addFilter('id', Number(params.get('id')));
      }

      if (params.get('dataSet')) {
        this.state = DataSet.decodeFromJson(params.get('dataSet'));
      }

      this.loadData(this.state);
    });
  }

  /**
   * Load data from API
   */
  loadData(state: DataStateChangeEvent = null) {
    this.state = state;
    this.apiQuery
      .setDataSetFilters(state)
      .addIncludes('company', 'company_website', 'event.city', 'event_group', 'hotel', 'client.country', 'sale');

    this.requestsService.index(this.apiQuery).subscribe((response: PaginatedResponse) => {
      this.requestsResponse = response;
      this.data = response.getGridData();
    });

    CustomKendoFilter.pushStateInStorage('RequestsListComponent', this.state);
  }

  edit(request: RequestModel) {
    this.requestsFormService.open(request).then(
      () => {
        this.loadData(this.state);
      },
      (err: any) => {
        log.debug(err);
      }
    );
  }

  delete(request: RequestModel) {
    this.requestsService.destroy(request.id).subscribe(
      () => {
        this.loadData(this.state);
      },
      (err: any) => {
        log.debug(err);
      }
    );
  }

  createSale(request: RequestModel) {
    this.salesFormService.open(null, request.id).then(() => {
      this.loadData(this.state);
    });
  }

  clearFilters() {
    this.router.navigate(['/enquiries']);
    this.apiQuery = new ApiQuery();
    this.loadData();
  }
}
